import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Accordion FAQ Component
const AccordionItem = ({ title, children, isOpen, onClick, highlightText }) => {
  const highlightedContent = useMemo(() => {
    if (!highlightText || !children) return children;
    const regex = new RegExp(`(${highlightText})`, 'gi');
    if (typeof children === 'string') {
      return children.split(regex).map((part, i) => 
        regex.test(part) ? (
          <mark key={i} className="bg-neon-orange/30 text-white px-1 rounded">{part}</mark>
        ) : part
      );
    }
    return children;
  }, [children, highlightText]);

  return (
    <div className="border border-neon-orange/20 rounded-xl overflow-hidden mb-3">
      <button
        onClick={onClick}
        className="w-full px-5 py-4 flex items-center justify-between text-left bg-dark-deeper/50 hover:bg-dark-deeper/70 transition-colors"
      >
        <span className="text-white font-semibold">{title}</span>
        <motion.svg
          className="w-5 h-5 text-neon-orange"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </motion.svg>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-5 py-4 bg-dark-base/50 text-gray-300 border-t border-neon-orange/10">
              {highlightedContent}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Search Bar Component
const SearchBar = ({ value, onChange }) => {
  return (
    <div className="relative mb-8">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search in this policy..."
        className="w-full px-4 py-3 pl-12 bg-dark-deeper border border-neon-orange/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-neon-orange/50 focus:shadow-[0_0_10px_rgba(253,128,46,0.2)] transition-all"
      />
      <svg
        className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-neon-orange transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Back to Home Button
const BackToHomeButton = () => {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Link
        to="/"
        className="inline-flex items-center gap-2 px-6 py-3 bg-dark-deeper border-2 border-neon-orange text-neon-orange font-semibold rounded-xl transition-all hover:shadow-[0_0_15px_rgba(253,128,46,0.4)]"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Home
      </Link>
    </motion.div>
  );
};

// Highlighted Text Component
const HighlightedText = ({ text, highlight }) => {
  if (!highlight) return <>{text}</>;
  
  const regex = new RegExp(`(${highlight})`, 'gi');
  const parts = text.split(regex);
  
  return (
    <>
      {parts.map((part, i) => 
        regex.test(part) ? (
          <mark key={i} className="bg-neon-orange/30 text-white px-1 rounded">{part}</mark>
        ) : part
      )}
    </>
  );
};

// Policy Page Layout Component
const PolicyPageLayout = ({ 
  title, 
  lastUpdated, 
  sections = [], 
  faqs = [] 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  return (
    <div className="min-h-screen bg-dark-base">
      {/* Header */}
      <motion.header
        className="bg-dark-deeper border-b-2 border-neon-orange py-8 px-4 pt-24 md:pt-28"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ boxShadow: '0 4px 20px rgba(253, 128, 46, 0.2)' }}
      >
        <div className="max-w-4xl mx-auto">
          <motion.h1 
            className="text-3xl md:text-4xl font-display font-bold text-white text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <span className="text-neon-orange">{title}</span>
          </motion.h1>
          {lastUpdated && (
            <motion.p 
              className="text-gray-400 text-center mt-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Last updated: {lastUpdated}
            </motion.p>
          )}
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Back Button */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <BackToHomeButton />
        </motion.div>

        {/* Search Bar */}
        <SearchBar value={searchQuery} onChange={setSearchQuery} />

        {/* Policy Sections */}
        <div className="space-y-8 mb-12">
          {sections.map((section, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-dark-deeper/30 rounded-2xl p-6 border border-neon-orange/10"
            >
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-neon-orange rounded-full" />
                <HighlightedText text={section.title} highlight={searchQuery} />
              </h2>
              
              {section.content && (
                <div className="text-gray-300 leading-relaxed space-y-4">
                  {section.content.map((paragraph, j) => (
                    <p key={j}>
                      <HighlightedText text={paragraph} highlight={searchQuery} />
                    </p>
                  ))}
                </div>
              )}

              {section.bullets && (
                <ul className="list-none space-y-2 mt-4">
                  {section.bullets.map((bullet, j) => (
                    <li key={j} className="text-gray-300 flex items-start gap-3">
                      <span className="w-1.5 h-1.5 bg-neon-orange rounded-full mt-2 flex-shrink-0" />
                      <HighlightedText text={bullet} highlight={searchQuery} />
                    </li>
                  ))}
                </ul>
              )}
            </motion.section>
          ))}
        </div>

        {/* FAQs */}
        {faqs.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <svg className="w-6 h-6 text-neon-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Frequently Asked Questions
            </h2>
            
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                title={faq.question}
                isOpen={openFaqIndex === i}
                onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                highlightText={searchQuery}
              >
                {faq.answer}
              </AccordionItem>
            ))}
          </motion.section>
        )}

        {/* Bottom Back Button */}
        <motion.div 
          className="mt-12 flex justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <BackToHomeButton />
        </motion.div>
      </main>
    </div>
  );
};

export default PolicyPageLayout;
